/**
 * THE ACCOUNT AREA — sign in, and the ONE Save and ONE Load. D-EZ-99.
 *
 * Howard, 17 Aug 2026: *"register with email and password and save their
 * leagues and reload them when they come back. Only one league at a time."*
 * And, 4 Oct 2026: one save action, not two.
 *
 * WHAT IS HERE NOW
 *
 *   Save league   — everything: the season in progress and every settled
 *                   season of this league (`leagueSlot.saveLeague`). Saving the
 *                   same league forward asks nothing; replacing a different
 *                   league, or going backwards, names what would be lost first.
 *   Load league   — the account's saved league, back on the workbench.
 *   On this device — the automatic copy the shell keeps after every race
 *                   (`localBackup.ts`), listed per account; "Download my
 *                   league" writes a file and "Open a league file" reads one.
 *
 * The Career tab's old "Save this season" (database rows) is gone: there is one
 * save, and it is this one.
 *
 * Every failure is a plain sentence from `leagueSlot.ts` or `auth.ts`. No sizes,
 * row counts or table names reach a player; the owner gets one diagnostics line.
 *
 * ⚠ NOTHING HERE SAVES TO THE ACCOUNT ON ITS OWN. Howard: *"Save and load
 * manually is the key to keeping progress straight without burning up the I/O
 * at supabase on auto saves."* The automatic copy is on THIS device only.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import '../account.css';
import {
  currentAccount,
  isOwner,
  onAccountChange,
  register,
  signIn,
  signOut,
  type Account,
  type AuthResult,
} from '../lib/auth.ts';
import {
  deleteSlot,
  describeOverwrite,
  describeSlotContents,
  formatSavedAt,
  loadLeague,
  mb,
  overwriteNeedsConfirm,
  peekSlot,
  saveLeague,
  type IncomingSave,
  type LoadedLeague,
  type SlotSummary,
} from '../lib/leagueSlot.ts';
import { downloadBytes, encodeLeagueFile, LEAGUE_FILE_EXTENSION, readLeagueFile } from '../lib/leagueFile.ts';
import { backupOwner, deleteBackup, describeBackup, listBackups, restoreBackup, type BackupSummary } from '../lib/localBackup.ts';
import type { LiveSeason } from '../lib/seasonSlot.ts';
import type { CareerSeason } from '../lib/careerRunner.ts';
import type { LeagueSettings } from '../lib/leagueSettings.ts';

/** Where a loaded league came from — the shell words its note by it. */
export type LoadSource = 'account' | 'device' | 'file';

export interface AccountBarProps {
  /** The latest settled season of the league on the workbench. */
  career?: CareerSeason | null;
  /** Earlier settled seasons of the same league, oldest first. */
  history?: readonly CareerSeason[];
  /** The season being played race by race, if any. */
  season?: LiveSeason | null;
  /** D-EZ-99 — which league this is. Everything saved carries it. */
  leagueId?: string;
  leagueName?: string;
  onLoad?: (league: LoadedLeague, source: LoadSource) => void;
  /** After a successful account save, with what the server stamped. */
  onSaved?: (summary: SlotSummary) => void;
  /** Bumped by the shell after each automatic device copy, so the list refreshes. */
  backupTick?: number;
  /** A league was deleted from this device (the shell refreshes its front-door "Continue"). */
  onDeviceChange?: () => void;
  /** D-EZ-103 — the league settings the next season starts with; saved with the league. */
  settings?: LeagueSettings;
  className?: string;
}

type Savable =
  | { kind: 'season'; season: LiveSeason; racesRun: number }
  | { kind: 'career'; career: CareerSeason }
  | { kind: 'nothing' };

/**
 * WHAT SAVE WOULD SAVE, decided in one pure place. The season in progress wins
 * when there is one — it is the work that exists nowhere else — and a season set
 * up but not yet raced is saveable (its named field exists nowhere else either).
 */
export function whatToSave(career: CareerSeason | null, season: LiveSeason | null): Savable {
  if (season) return { kind: 'season', season, racesRun: season.state.races.length };
  if (career) return { kind: 'career', career };
  return { kind: 'nothing' };
}

function incoming(savable: Savable, leagueName: string, leagueId: string | undefined): IncomingSave | null {
  if (savable.kind === 'season') {
    return { kind: 'season', leagueId, leagueName, seasonNumber: savable.season.seasonNumber, racesRun: savable.racesRun };
  }
  if (savable.kind === 'career') {
    return { kind: 'career', leagueId, leagueName, seasonNumber: savable.career.seasonNumber, racesRun: null };
  }
  return null;
}

/** The id the cloud nudge focuses. `App.tsx` imports this rather than repeating it. */
export const SAVE_BUTTON_ID = 'acct-save-league';

type Note = { tone: 'ok' | 'bad' | 'plain'; text: string } | null;
type Mode = 'sign-in' | 'register';
type Busy = null | 'auth' | 'save' | 'load' | 'clear' | 'file' | 'device';

export default function AccountBar({
  career,
  history = [],
  season,
  leagueId,
  leagueName = 'RASCAL',
  onLoad,
  onSaved,
  backupTick = 0,
  onDeviceChange,
  settings,
  className,
}: AccountBarProps) {
  const [account, setAccount] = useState<Account | null>(null);
  const [checked, setChecked] = useState(false);
  const [sessionError, setSessionError] = useState<string | null>(null);

  const [mode, setMode] = useState<Mode>('sign-in');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState<Busy>(null);
  const [note, setNote] = useState<Note>(null);
  /** Owner only: what the last save sent. */
  const [diag, setDiag] = useState<string | null>(null);

  const [slot, setSlot] = useState<SlotSummary | null>(null);
  const [slotChecked, setSlotChecked] = useState(false);
  const [slotError, setSlotError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<null | 'overwrite' | 'clear'>(null);
  const [backups, setBackups] = useState<BackupSummary[]>([]);
  /** The device league whose "Delete" was pressed and is waiting for "Yes, delete it". */
  const [forgetting, setForgetting] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    let live = true;
    currentAccount()
      .then((a) => {
        if (!live) return;
        setAccount(a);
        setChecked(true);
      })
      .catch((e: { message?: string }) => {
        if (!live) return;
        setSessionError(e?.message ?? 'Your saved sign-in could not be read.');
        setChecked(true);
      });
    const off = onAccountChange((a) => {
      if (!live) return;
      setAccount(a);
      setChecked(true);
    });
    return () => {
      live = false;
      off();
    };
  }, []);

  const refreshSlot = useCallback(async () => {
    if (!account) {
      setSlot(null);
      setSlotChecked(false);
      setSlotError(null);
      return;
    }
    const r = await peekSlot();
    if (!alive.current) return;
    if (r.ok) {
      setSlot(r.summary);
      setSlotError(null);
    } else {
      setSlot(null);
      setSlotError(r.message);
    }
    setSlotChecked(true);
  }, [account]);

  useEffect(() => {
    setConfirming(null);
    void refreshSlot();
  }, [refreshSlot]);

  // ---- the copies on this device, for whoever is here ----------------------
  const owner = backupOwner(account?.id ?? null);
  useEffect(() => {
    let live = true;
    void listBackups(owner).then((list) => live && setBackups(list));
    return () => {
      live = false;
    };
  }, [owner, backupTick]);

  // ---- actions ---------------------------------------------------------------

  const submitAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy('auth');
    setNote(null);
    const result: AuthResult = mode === 'register' ? await register(email, password) : await signIn(email, password);
    if (!alive.current) return;
    setBusy(null);
    if (!result.ok) {
      setNote({ tone: 'bad', text: result.message });
      if (result.code === 'email-already-registered') setMode('sign-in');
      return;
    }
    setPassword('');
    if (result.account) {
      setAccount(result.account);
      setNote({ tone: 'ok', text: `Signed in as ${result.account.email}.` });
    } else {
      setNote({ tone: 'plain', text: result.note ?? 'Check your email to confirm the address, then sign in.' });
    }
  };

  const doSignOut = async () => {
    setBusy('auth');
    const r = await signOut();
    if (!alive.current) return;
    setBusy(null);
    if (!r.ok) {
      setNote({ tone: 'bad', text: `${r.message} You are still signed in.` });
      return;
    }
    setAccount(null);
    setSlot(null);
    setSlotChecked(false);
    setNote({ tone: 'plain', text: 'Signed out. The game keeps working, and this device keeps its copy.' });
  };

  const savable = whatToSave(career ?? null, season ?? null);
  const going = incoming(savable, leagueName, leagueId);
  const league = {
    leagueId: leagueId ?? '',
    leagueName,
    season: season ?? null,
    career: career ?? null,
    history,
    ...(settings ? { settings } : {}),
  };

  const doSave = async () => {
    setBusy('save');
    setNote(null);
    const r = await saveLeague(league);
    if (!alive.current) return;
    setBusy(null);
    setConfirming(null);
    if (!r.ok) {
      setNote({ tone: 'bad', text: r.message });
      return;
    }
    setSlot(r.summary);
    onSaved?.(r.summary);
    setNote({ tone: 'ok', text: `Saved ${describeSlotContents(r.summary)}.` });
    setDiag(
      `Sent ${mb(r.bytes)} MB · ${r.seasonsWritten} of ${r.seasonsHeld} season${r.seasonsHeld === 1 ? '' : 's'}` +
        (r.racesWritten !== null ? ` · ${r.racesWritten} of ${r.racesHeld} races` : ''),
    );
  };

  const pressSave = () => {
    if (slot && going && overwriteNeedsConfirm(slot, going)) setConfirming('overwrite');
    else void doSave();
  };

  const opened = (league: LoadedLeague, source: LoadSource) => {
    const where = source === 'account' ? 'from your account' : source === 'device' ? 'from this device' : 'from the file';
    setNote({
      tone: 'ok',
      text:
        league.kind === 'season'
          ? `Opened ${league.leagueName} ${where} — season ${league.seasonNumber}, race ${league.racesRun + 1} next.`
          : `Opened ${league.leagueName} ${where} — season ${league.seasonNumber}, finished.`,
    });
    onLoad?.(league, source);
  };

  const doLoad = async () => {
    setBusy('load');
    setNote(null);
    const r = await loadLeague();
    if (!alive.current) return;
    setBusy(null);
    if (!r.ok) {
      setNote({ tone: 'bad', text: r.message });
      return;
    }
    opened(r.league, 'account');
  };

  const doClear = async () => {
    setBusy('clear');
    setNote(null);
    const r = await deleteSlot();
    if (!alive.current) return;
    setBusy(null);
    setConfirming(null);
    if (!r.ok) {
      setNote({ tone: 'bad', text: r.message });
      return;
    }
    setSlot(null);
    setNote({
      tone: 'plain',
      text: r.removed ? `Deleted ${r.removed.leagueName} from your account.` : 'There was nothing saved to delete.',
    });
  };

  const doDownload = async () => {
    setBusy('file');
    setNote(null);
    const r = await encodeLeagueFile(league);
    if (!alive.current) return;
    setBusy(null);
    if ('ok' in r) {
      setNote({ tone: 'bad', text: r.message });
      return;
    }
    downloadBytes(r.bytes, r.name);
    setNote({ tone: 'ok', text: `Downloaded ${r.name}. Keep it somewhere safe — Open a league file brings it back.` });
  };

  const doOpenFile = async (file: File | undefined) => {
    if (!file) return;
    setBusy('file');
    setNote(null);
    const r = await readLeagueFile(new Uint8Array(await file.arrayBuffer()));
    if (!alive.current) return;
    setBusy(null);
    if (fileInput.current) fileInput.current.value = '';
    if ('ok' in r) {
      setNote({ tone: 'bad', text: r.message });
      return;
    }
    opened(r, 'file');
  };

  const doRestore = async (b: BackupSummary) => {
    setBusy('device');
    setNote(null);
    const r = await restoreBackup(owner, b.leagueId);
    if (!alive.current) return;
    setBusy(null);
    if ('ok' in r) {
      setNote({ tone: 'bad', text: r.message });
      return;
    }
    opened(r, 'device');
  };

  /** Delete one league from this device — trial leagues included. Cannot be undone. */
  const doForget = async (b: BackupSummary) => {
    setBusy('device');
    setNote(null);
    try {
      await deleteBackup(owner, b.leagueId);
      setBackups((list) => list.filter((x) => x.leagueId !== b.leagueId));
      setNote({ tone: 'ok', text: `${b.leagueName} was deleted from this device.` });
      onDeviceChange?.();
    } catch {
      setNote({ tone: 'bad', text: `Couldn't delete ${b.leagueName} from this device. Try again.` });
    } finally {
      if (alive.current) {
        setBusy(null);
        setForgetting(null);
      }
    }
  };

  // ---- render ----------------------------------------------------------------

  if (!checked) {
    return (
      <div className={cx('acct', className)} aria-busy="true">
        <span className="acct__dim">Checking your sign-in…</span>
      </div>
    );
  }

  const working = busy !== null;
  const device = (
    <div className="acct__device">
      <p className="acct__label">On this device</p>
      {backups.length === 0 ? (
        <p className="acct__dim">Your league is copied here automatically after every race.</p>
      ) : (
        <ul className="acct__backups">
          {backups.map((b) => (
            <li key={b.leagueId}>
              <span>
                <b>{describeBackup(b)}</b>
                <span className="acct__dim"> · copied {formatSavedAt(b.savedAt)}</span>
              </span>
              {forgetting === b.leagueId ? (
                <span className="acct__forget" role="group" aria-label={`Delete ${b.leagueName}?`}>
                  <span className="acct__forget-q">
                    Delete this league from this device?{' '}
                    {b.leagueId === leagueId
                      ? "It's the league you have open — it will be copied here again after your next race."
                      : "This can't be undone."}
                  </span>
                  <button className="btn btn--danger btn--tiny" type="button" onClick={() => void doForget(b)} disabled={working}>
                    Yes, delete it
                  </button>
                  <button className="btn btn--quiet btn--tiny" type="button" onClick={() => setForgetting(null)} disabled={working}>
                    Keep it
                  </button>
                </span>
              ) : (
                <span className="acct__backup-acts">
                  <button className="btn btn--quiet btn--tiny" type="button" onClick={() => void doRestore(b)} disabled={working}>
                    {busy === 'device' ? 'Opening…' : 'Open'}
                  </button>
                  <button className="btn btn--quiet btn--tiny" type="button" onClick={() => setForgetting(b.leagueId)} disabled={working}>
                    Delete
                  </button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="acct__controls">
        <button className="btn" type="button" onClick={() => void doDownload()} disabled={working || savable.kind === 'nothing'}>
          {busy === 'file' ? 'Working…' : 'Download my league'}
        </button>
        <button className="btn btn--quiet" type="button" onClick={() => fileInput.current?.click()} disabled={working}>
          Open a league file
        </button>
        <input
          ref={fileInput}
          className="sr-only"
          type="file"
          accept={`${LEAGUE_FILE_EXTENSION},application/gzip,application/json,.json,.gz`}
          aria-label="Open a league file"
          tabIndex={-1}
          onChange={(e) => void doOpenFile(e.target.files?.[0])}
        />
      </div>
    </div>
  );

  return (
    <div className={cx('acct', className)}>
      {account ? (
        <>
          <div className="acct__who">
            <span className="acct__label">Signed in</span>
            <span className="acct__email">{account.email}</span>
            <button className="acct__switch" type="button" onClick={() => void doSignOut()} disabled={working}>
              {busy === 'auth' ? 'Signing out…' : 'Sign out'}
            </button>
          </div>

          <div className="acct__slot">
            {!slotChecked && !slotError && <span className="acct__dim">Looking for your saved league…</span>}
            {slotError && <span className="acct__msg acct__msg--bad">{slotError}</span>}
            {slotChecked && !slotError && !slot && (
              <span className="acct__dim">Nothing saved to your account yet. Save league keeps every season.</span>
            )}
            {slot && (
              <span className="acct__have">
                Saved: <b>{describeSlotContents(slot)}</b>
                <span className="acct__dim"> · {formatSavedAt(slot.savedAt)}</span>
              </span>
            )}
          </div>

          <div className="acct__controls">
            <button
              className="btn btn--green"
              type="button"
              onClick={pressSave}
              id={SAVE_BUTTON_ID}
              disabled={working || savable.kind === 'nothing' || !leagueId || confirming !== null}
            >
              {busy === 'save' ? 'Saving…' : 'Save league'}
            </button>
            <button className="btn" type="button" onClick={() => void doLoad()} disabled={working || !slot || confirming !== null}>
              {busy === 'load' ? 'Loading…' : 'Load league'}
            </button>
            {slot && (
              <button className="btn btn--quiet" type="button" onClick={() => setConfirming('clear')} disabled={working || confirming !== null}>
                Delete saved league
              </button>
            )}
          </div>

          {savable.kind === 'nothing' && (
            <p className="acct__dim acct__why">
              Nothing to save yet — start a league on Home.
            </p>
          )}

          {confirming === 'overwrite' && slot && going && (
            <Confirm
              heading="Replace your saved league?"
              body={describeOverwrite(slot, going)}
              confirmLabel="Replace it"
              busy={busy === 'save'}
              onConfirm={() => void doSave()}
              onCancel={() => setConfirming(null)}
            />
          )}
          {confirming === 'clear' && slot && (
            <Confirm
              heading="Delete your saved league?"
              body={`This deletes ${describeSlotContents(slot)} from your account. The copy on this device stays. There is no undo.`}
              confirmLabel="Delete it"
              busy={busy === 'clear'}
              onConfirm={() => void doClear()}
              onCancel={() => setConfirming(null)}
            />
          )}
          {isOwner(account) && diag && <p className="acct__dim acct__diag">{diag}</p>}
        </>
      ) : (
        <form className="acct__form" onSubmit={submitAuth}>
          <p className="acct__pitch">
            <b>{mode === 'register' ? 'Register' : 'Sign in'}</b> to save your league to your account and pick it up anywhere.{' '}
            <span className="acct__dim">The game works without one.</span>
          </p>
          {sessionError && (
            <p className="acct__msg acct__msg--bad" role="status">
              {sessionError} Signing in again will fix it.
            </p>
          )}
          <label className="sr-only" htmlFor="acct-email">
            Email address
          </label>
          <input
            id="acct-email"
            className="acct__in"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={busy === 'auth'}
          />
          <label className="sr-only" htmlFor="acct-pw">
            Password
          </label>
          <input
            id="acct-pw"
            className="acct__in"
            type="password"
            autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
            placeholder="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={busy === 'auth'}
          />
          <button className="btn btn--hot" type="submit" disabled={busy === 'auth'}>
            {busy === 'auth' ? (mode === 'register' ? 'Registering…' : 'Signing in…') : mode === 'register' ? 'Register' : 'Sign in'}
          </button>
          <button
            className="acct__switch"
            type="button"
            onClick={() => {
              setMode(mode === 'register' ? 'sign-in' : 'register');
              setNote(null);
            }}
            disabled={busy === 'auth'}
          >
            {mode === 'register' ? 'I already have an account' : 'Register instead'}
          </button>
        </form>
      )}

      {device}

      {note && (
        <p className={cx('acct__msg', note.tone === 'bad' && 'acct__msg--bad', note.tone === 'ok' && 'acct__msg--ok')} role="status">
          {note.text}
        </p>
      )}
    </div>
  );
}

/** A confirmation that names what it would replace. Cancel holds focus. */
function Confirm({
  heading,
  body,
  confirmLabel,
  busy,
  onConfirm,
  onCancel,
}: {
  heading: string;
  body: string;
  confirmLabel: string;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);
  return (
    <div className="acct__confirm" role="alertdialog" aria-label={heading}>
      <h4 className="acct__confirm-h">{heading}</h4>
      <p className="acct__confirm-body">{body}</p>
      <div className="acct__controls">
        <button ref={cancelRef} className="btn" type="button" onClick={onCancel} disabled={busy}>
          Keep what I have
        </button>
        <button className="btn btn--hot" type="button" onClick={onConfirm} disabled={busy}>
          {busy ? 'Working…' : confirmLabel}
        </button>
      </div>
    </div>
  );
}

const cx = (...parts: (string | false | null | undefined)[]): string => parts.filter(Boolean).join(' ');
